/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import Markdown from 'markdown-to-jsx';

import { jsx } from '@atlaskit/css';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AIDisclaimer, AIFeedbackComponent } from '@atlassian/ai-components';
import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

const MAU_CRITERIA_DWELL_TIME = 3000;

type Props = {
	contentId: string;
	text?: string;
	showFooter?: boolean;
	source?: string;
	isHidden?: boolean;
	maxLines: number;
};

export const AISnippetContent = ({
	text,
	contentId,
	showFooter = true,
	source,
	isHidden,
	maxLines,
}: Props) => {
	const ref = useRef<HTMLDivElement>(null);
	const { getDwellTime } = usePopupDwellTime(ref, 250);
	const { trackAIResultView } = useAIEventsInstrumentation();
	// This is needed to keep the snippet value in scope when running the unmount effect
	const snippetValueRef = useRef(text);

	useEffect(() => {
		const currentText = snippetValueRef.current;

		return () => {
			const dwellTime = getDwellTime();
			if (!isHidden) {
				if (showFooter && currentText && dwellTime >= MAU_CRITERIA_DWELL_TIME) {
					trackAIResultView({ attributes: { dwellTime, source, wasShownAIContent: true } });
				} else {
					trackAIResultView({
						attributes: { dwellTime, source, wasShownAIContent: showFooter && !!currentText },
						doesNotMeetMAUCriteria: true,
					});
				}
			}
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Stack space="space.100" ref={ref}>
			<Text size="small" maxLines={maxLines}>
				<Markdown>{text || ''}</Markdown>
			</Text>
			{showFooter && text && (
				<Inline space="space.100">
					<AIDisclaimer />
					<AIFeedbackComponent contentId={contentId} />
				</Inline>
			)}
		</Stack>
	);
};
